import React, { useState } from "react";
import theme from "../../data/theme";
import { epochFromLocalInput } from "../../utils/liveBoard";

/**
 * LiveBoardSetup — filled in once, before the auction starts.
 *
 * League name, budget, teams, roster size, and the team names TYPED IN DRAFT
 * ORDER. Prefilled from the active saved league, and the names are prefilled
 * from last year's board where one exists — leagues keep their managers, and
 * retyping eleven names while people wait is the friction this replaces.
 */
/**
 * ══════════════════════════════════════════════════════════════════════
 * 🔴 NO BOARD IS NAMED "default".
 *
 * The app's DEFAULT profile has no name — the header prints the word DEFAULT
 * as a LABEL for `activeLeagueName == null`. Most people never create a named
 * league, so this field arrived empty, they left it empty, and `startBoard`'s
 * last-resort `|| 'default'` became the league's actual name. Every share link
 * and every TV in those rooms read "default · $200 · 10 teams".
 *
 * So the name is REQUIRED here, at the one screen where a human is looking at
 * the field. The fallback in useLiveBoard stays exactly as it is: it guards
 * every OTHER caller of startBoard, including ones nobody has written yet, and
 * this screen simply can no longer reach it.
 *
 * ⚠️ THE MATCH IS ON THE PREFILL, NOT ON THE PROFILE. This component is handed
 * a string and never sees `activeLeagueName`, so "the default profile" is
 * recognised as "" or the word default. A saved league genuinely called
 * "Default" is the one false positive, and it costs its owner one retype.
 * ══════════════════════════════════════════════════════════════════════
 */
const isDefaultProfileName = (name) =>
  !name || String(name).trim().toLowerCase() === "default";

/**
 * ══════════════════════════════════════════════════════════════════════
 * 🔴 THE ROSTER IS SET HERE, WHERE SOMEBODY IS LOOKING AT IT.
 *
 * The board's `positions` used to come silently from the active league's
 * settings — a screen most people have never opened. So their board rostered
 * 1QB/2RB/3WR when their league runs two flexes or a superflex, every card was
 * the wrong shape, PLAYERS LEFT was the wrong depth, and nothing on screen said
 * why. It is not a setting anybody thinks to go and check; it is part of
 * describing your league, and describing your league is what this screen is.
 *
 * 🔴 THE ORDER AND THE KEYS ARE `POS_KEYS`' OWN. positionsForRosterSize,
 * buildRosterSlots and sanitizePositions all iterate the same nine in the same
 * order — a tenth field here that they do not know about would be typed in and
 * silently dropped at the write.
 *
 * ⚠️ IT DOES NOT WRITE BACK TO THE LEAGUE. A live board is one night; a saved
 * league is every mock draft after it. Someone correcting tonight's roster must
 * not silently re-shape the simulator too.
 * ══════════════════════════════════════════════════════════════════════
 */
const SLOT_FIELDS = ["QB", "RB", "WR", "TE", "FLEX", "SFLEX", "DEF", "K", "BN"];

/**
 * Opens showing TODAY's board, not a blank form. The bench is what the hook's
 * `positionsForRosterSize` would have handed the old roster-size field, so the
 * first render of this block reproduces exactly the board the user would have
 * got without it — then they correct it.
 */
function seedSlots(defaults) {
  const src = defaults.positions || {};
  const out = {};
  let starters = 0;
  for (const k of SLOT_FIELDS) {
    if (k === "BN") continue;
    out[k] = Math.max(0, Math.round(Number(src[k]) || 0));
    starters += out[k];
  }
  const bn = Math.round(Number(src.BN));
  out.BN = Number.isFinite(bn) && bn > 0
    ? bn
    : Math.max(0, Math.round(Number(defaults.rosterSize) || 0) - starters);
  return out;
}

export default function LiveBoardSetup({ defaults, onStart }) {
  // Prefilled from a REAL saved league, blank for the default profile. A
  // prefill somebody has to notice and delete is worse than an empty field
  // with a placeholder telling them what goes in it.
  const [leagueName, setLeagueName] = useState(
    isDefaultProfileName(defaults.leagueName) ? "" : defaults.leagueName);
  const [budget, setBudget] = useState(String(defaults.budget));
  // 🔴 THE ROSTER SIZE IS NOT TYPED ANY MORE, IT IS THE SUM. A separately typed
  // total and a per-slot layout are two numbers that can disagree, and when
  // they did the same screen showed "12 slots left" over a 16-slot grid.
  const [slots, setSlots] = useState(() => seedSlots(defaults));
  const [userTeam, setUserTeam] = useState(-1);
  // 🔴 OPTIONAL, AND NOTHING GATES ON IT. Empty is the normal state and means
  // no countdown exists on any screen. It is deliberately absent from `valid`
  // below — a missing date must never be a reason somebody cannot start.
  const [draftAt, setDraftAt] = useState("");

  // 🔴 THE MASTER LIST. The count field used to bind value={names.length} —
  // derived, clamped state — so typing "10" passed through "1", clamped to 2,
  // and rewrote the field: the count was unreachable. It is a <select> now, and
  // the names live in a list that is never truncated, only SLICED for display.
  // Going 12 -> 10 -> 12 therefore gives back the two names you already typed
  // instead of stock placeholders.
  const [allNames, setAllNames] = useState(() => {
    const seed = [...defaults.teamNames];
    while (seed.length < 20) seed.push("");
    return seed;
  });
  const [count, setCount] = useState(defaults.teamNames.length || 10);
  const names = allNames.slice(0, count);

  const budgetNum = Number(budget);
  const rosterNum = SLOT_FIELDS.reduce((t, k) => t + (Number(slots[k]) || 0), 0);
  const filled = names.map(n => n.trim()).filter(Boolean);
  // 🔴 THE LEAGUE NAME IS REQUIRED — see the header. It is the name on the
  // share link and on the TV, and there is no correct value to invent for it.
  const namedLeague = leagueName.trim().length > 0;
  const valid = namedLeague && filled.length === names.length && budgetNum > 0 && rosterNum > 0
    && new Set(filled.map(n => n.toLowerCase())).size === filled.length;

  const start = () => {
    if (!valid) return;
    onStart({
      leagueName: leagueName.trim(), budget: budgetNum, rosterSize: rosterNum,
      // What was typed, not what the league settings say. The hook's
      // positionsForRosterSize gives the bench the remainder, and the
      // remainder is BN itself here, so this round-trips unchanged.
      positions: { ...slots }, userTeam,
      // Converted HERE, on the device that typed it, where "7pm" still means
      // 7pm to a specific person in a specific place. Everything downstream
      // handles an instant and never a wall clock.
      draftAt: epochFromLocalInput(draftAt),
      teamNames: names.map(n => n.trim()),
    });
  };

  const field = {
    background: theme.colors.bg.raised, border: `1px solid ${theme.colors.border.default}`,
    borderRadius: theme.radius.md, color: theme.colors.text.primary, fontFamily: "inherit",
    fontSize: theme.fontSize.sub, padding: "10px 12px", outline: "none", width: "100%",
  };
  const label = {
    fontSize: theme.fontSize.micro, textTransform: "uppercase", letterSpacing: "0.08em",
    color: theme.colors.text.faint, fontWeight: theme.fontWeight.bold, marginBottom: 4, display: "block",
  };

  return (
    <div style={{ maxWidth: 760, margin: "0 auto" }}>
      <h2 style={{
        fontFamily: theme.fonts.display, fontSize: theme.fontSize.title, color: theme.colors.text.primary,
        margin: "0 0 6px", fontWeight: theme.fontWeight.heavy,
      }}>Set up your live auction</h2>
      <p data-testid="lb-intro" style={{ color: theme.colors.text.muted, fontSize: theme.fontSize.body, margin: "0 0 22px", lineHeight: 1.5 }}>
        Type each sale as it happens and this builds every roster, every budget, and who is
        still on the board. The board is published to a private share link, so your league can
        watch it live on any screen. Only this device enters picks.
      </p>

      {/* ══════════════════════════════════════════════════════════════
          🔴 HOW IT WORKS, BEFORE THE FIRST FIELD. The intro above says what the
          board IS; this says what the next two hours actually look like, which
          is the question somebody weighing whether to run their real auction on
          a beta is asking. It is above the form because a person who reads it
          AFTER typing eleven team names has already decided.

          The last two lines are the ones that matter most: every operator's
          real fear is mistyping a price in front of the room, or the guy who
          left at pick 90. Both have an answer, so both are said here rather
          than discovered.
          ══════════════════════════════════════════════════════════════ */}
      <ol data-testid="lb-how" style={{
        color: theme.colors.text.muted, fontSize: theme.fontSize.body,
        lineHeight: 1.55, margin: "0 0 18px", paddingLeft: 20,
        display: "flex", flexDirection: "column", gap: 6, maxWidth: 620,
      }}>
        <li>Set up your league. Teams, budget, roster, team names. Thirty seconds.</li>
        <li>Type each sale as it happens. One device, phone or laptop. Player, price, team, Enter.</li>
        <li>Share the watch link. Everyone sees every roster, every budget and max bid, who is left, and your cheat sheet crossing itself off, live on any phone or the TV.</li>
        <li>Made a mistake? Undo takes back the last sale. Someone left early? Skip their nomination with the arrows next to TURN.</li>
      </ol>
      <p style={{
        color: theme.colors.text.faint, fontSize: theme.fontSize.caption,
        lineHeight: 1.55, margin: "0 0 22px", maxWidth: 620,
      }}>
        Nobody can change anything from the link. The link expires two weeks after the draft.
        {" "}
        <a
          href="/watch/sample" target="_blank" rel="noopener noreferrer"
          style={{ color: theme.colors.brand.accent, textDecoration: "none" }}
        >See a sample board</a>
      </p>

      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 12, marginBottom: 22 }}>
        <div>
          <label style={label} htmlFor="lb-league">League name</label>
          <input id="lb-league" style={field} value={leagueName} onChange={e => setLeagueName(e.target.value)}
            placeholder="Your league's name" />
        </div>
        <div>
          <label style={label} htmlFor="lb-budget">Budget</label>
          <input id="lb-budget" style={field} value={budget} inputMode="numeric"
            onChange={e => setBudget(e.target.value.replace(/[^0-9]/g, ""))} />
        </div>
        <div>
          <label style={label} htmlFor="lb-teams">Teams</label>
          <select id="lb-teams" style={{ ...field, cursor: "pointer" }} value={count}
            onChange={e => setCount(Number(e.target.value))}>
            {[8, 10, 12, 14, 16].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
      </div>

      <div style={{ marginBottom: 22, maxWidth: 320 }}>
        <label style={label} htmlFor="lb-draft-at">
          Draft date &amp; time <span style={{
            textTransform: "none", letterSpacing: 0, color: theme.colors.text.faint,
            fontWeight: theme.fontWeight.regular,
          }}>· optional</span>
        </label>
        <input
          id="lb-draft-at" type="datetime-local" style={{ ...field, cursor: "pointer" }}
          value={draftAt} onChange={e => setDraftAt(e.target.value)}
        />
        <div style={{ fontSize: theme.fontSize.micro, color: theme.colors.text.faint, marginTop: 4, lineHeight: 1.5 }}>
          Your local time. Everyone who opens the share link sees it counted down in theirs.
        </div>
      </div>

      {/* Above the team names because it describes the LEAGUE, and the names
          are the last thing typed before starting. */}
      <label style={{ ...label, marginBottom: 8 }} id="lb-roster-label">
        Roster <span style={{
          textTransform: "none", letterSpacing: 0, color: theme.colors.brand.accent,
          fontWeight: theme.fontWeight.bold,
        }} data-testid="lb-roster-total">
          · {rosterNum} {rosterNum === 1 ? "player" : "players"} per team
        </span>
      </label>
      <div
        data-testid="lb-roster-slots"
        role="group"
        aria-labelledby="lb-roster-label"
        style={{
          display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(72px, 1fr))",
          gap: 8, marginBottom: 8,
        }}
      >
        {SLOT_FIELDS.map(k => (
          <div key={k}>
            <label style={{ ...label, marginBottom: 3 }} htmlFor={`lb-slot-${k}`}>{k}</label>
            <input
              id={`lb-slot-${k}`}
              style={{ ...field, textAlign: "center", padding: "8px 6px" }}
              value={String(slots[k])}
              inputMode="numeric"
              aria-label={`${k} slots`}
              onChange={e => {
                const v = e.target.value.replace(/[^0-9]/g, "");
                setSlots(prev => ({ ...prev, [k]: v === "" ? 0 : Number(v) }));
              }}
            />
          </div>
        ))}
      </div>
      <div style={{
        fontSize: theme.fontSize.caption, color: theme.colors.text.muted,
        lineHeight: 1.55, marginBottom: 20, maxWidth: 620,
      }}>
        Set this to match your league exactly. It decides every roster on the board.
      </div>

      <label style={{ ...label, marginBottom: 8 }}>
        Team names, in draft order <span style={{ textTransform: "none", letterSpacing: 0, color: theme.colors.text.faint, fontWeight: theme.fontWeight.regular }}>· tap "me" to mark your own seat</span>
      </label>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))", gap: 8, marginBottom: 20 }}>
        {names.map((n, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{
              fontSize: theme.fontSize.micro, color: theme.colors.text.faint, width: 18,
              textAlign: "right", fontVariantNumeric: "tabular-nums",
            }}>{i + 1}</span>
            <input
              style={field} value={n} placeholder={`Team ${i + 1}`}
              aria-label={`Team ${i + 1} name`}
              onChange={e => setAllNames(prev => prev.map((v, j) => (j === i ? e.target.value : v)))}
            />
            <button
              type="button"
              onClick={() => setUserTeam(u => (u === i ? -1 : i))}
              aria-label={`Mark team ${i + 1} as mine`}
              aria-pressed={userTeam === i}
              style={{
                background: userTeam === i ? theme.colors.brand.orange : "transparent",
                border: `1px solid ${userTeam === i ? theme.colors.brand.orange : theme.colors.border.default}`,
                borderRadius: theme.radius.sm, color: userTeam === i ? "#fff" : theme.colors.text.faint,
                fontFamily: "inherit", fontSize: theme.fontSize.micro, fontWeight: theme.fontWeight.bold,
                padding: "6px 8px", cursor: "pointer", flexShrink: 0,
              }}
            >me</button>
          </div>
        ))}
      </div>

      {!valid && (
        <div style={{ color: theme.colors.status.warning, fontSize: theme.fontSize.caption, marginBottom: 12 }}>
          {!namedLeague
            ? "Your league needs a name. It goes on the share link and on the TV."
            : filled.length !== names.length
              ? "Every team needs a name."
              : new Set(filled.map(x => x.toLowerCase())).size !== filled.length
                ? "Two teams have the same name. The type-ahead could not tell them apart."
                : rosterNum <= 0
                  ? "Every team needs at least one roster slot."
                  : "The budget must be greater than zero."}
        </div>
      )}

      {/* 🔴 B4 — SAID PLAINLY, WHERE THE LINK IS CREATED. The team names are
          real people, typed by the user. The URL is unguessable but anyone
          holding it can read the board. One line, no modal, no dark pattern. */}
      <div style={{
        fontSize: theme.fontSize.caption, color: theme.colors.text.muted,
        lineHeight: 1.55, marginBottom: 16, maxWidth: 620,
      }}>
        Starting the board creates a private read-only link you can share with your league.
        The link is unguessable, but anyone who has it can see the board, including the team
        names you typed above. Nobody can enter or edit a pick from the link; only this device
        does that. What gets published is player names, prices and team names, nothing about
        you. The share link expires 14 days after the draft, and an anonymous record of the
        results (players, prices, team names, nothing about you) is kept as part of the site's
        auction data.
      </div>

      <button
        onClick={start} disabled={!valid}
        style={{
          background: valid ? theme.colors.brand.gradientBtn : theme.colors.bg.raised,
          color: valid ? "#fff" : theme.colors.text.faint, border: "none", borderRadius: theme.radius.lg,
          padding: "13px 26px", fontFamily: "inherit", fontSize: theme.fontSize.sub,
          fontWeight: theme.fontWeight.heavy, cursor: valid ? "pointer" : "default",
        }}
      >Start the board</button>
    </div>
  );
}
